import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

// CNPJ/CPF formatado pro label do seletor de empresa (vem do tb_empresa).
const fmtCnpj = (c?: string | null): string => {
  if (!c) return '';
  const d = c.replace(/\D/g, '');
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return c;
};

export type IContaCilStatus =
  | 'processando'
  | 'concluido'
  | 'erro'
  | 'a_identificar'
  | 'aguardando_titular'
  | 'aguardando_validacao'
  | 'quarentena';

export interface IContaCilImportarResult {
  import_id: number;
  status: IContaCilStatus;
  formato: string;
  nome_arquivo: string;
  sha256: string;
  duplicado: boolean;
  importado_por_email?: string | null;
  importado_por_origem?: 'escritorio' | 'cliente' | null;
  mensagem: string;
}

export interface IEmpresaOption {
  value: number;
  label: string;
  external_id: string;
}

interface IApiEnvelope<T> {
  data: T;
  message?: string;
  meta?: unknown;
}

interface IEmpresaApiItem {
  id: number;
  nome: string;
  cnpj?: string;
  razao_social?: string;
  external_id: string;
}

const importarArquivo = async (params: {
  file: File;
  empresaId?: number;
}): Promise<IContaCilImportarResult> => {
  const form = new FormData();
  form.append('file', params.file);
  // Empresa e opcional: o ETL detecta pelo CNPJ do extrato. Se nao
  // identificar, o import fica 'a_identificar' pra atribuicao manual.
  if (params.empresaId != null) form.append('empresa_id', String(params.empresaId));
  const res = await api.post<IApiEnvelope<IContaCilImportarResult>>(
    '/api/v1/contacil/importar/',
    form,
  );
  return res.data.data;
};

// Label do seletor de empresa: id (external_id) · razão · CNPJ (tudo do tb_empresa).
const empresaLabel = (e: IEmpresaApiItem): string =>
  [e.external_id, e.razao_social || e.nome, fmtCnpj(e.cnpj)].filter(Boolean).join(' · ');

/**
 * Busca empresas para o seletor (AsyncSelect). Usa o endpoint /contacil/
 * empresas/busca em modo `flex` (default) — grupo-scoped (mesma permissao do
 * app) e flexivel: razao_social, nome, external_id ou cnpj. Substitui o
 * /api/v1/empresas/ legado pra propagar o escopo de grupo a todos os
 * seletores do modulo Contabil.
 */
export const buscarEmpresasOptions = async (inputValue: string): Promise<IEmpresaOption[]> => {
  const termo = inputValue.trim();
  if (!termo) return [];
  const res = await api.get<IApiEnvelope<IEmpresaApiItem[]>>('/api/v1/contacil/empresas/busca', {
    params: { q: termo, size: 20 },
  });
  const items = res.data?.data ?? [];
  return items.map(e => ({ value: e.id, label: empresaLabel(e), external_id: e.external_id }));
};

/**
 * Loader debounced (300ms) para o AsyncSelect de empresa: não carrega lista
 * no clique (só busca ao digitar) e cancela requests intermediários. Cada
 * AsyncSelect deve ter sua própria instância (chamar via useMemo).
 */
export function criarBuscaEmpresas() {
  let timer: ReturnType<typeof setTimeout>;
  return (input: string): Promise<IEmpresaOption[]> =>
    new Promise(resolve => {
      clearTimeout(timer);
      if (!input.trim()) {
        resolve([]);
        return;
      }
      timer = setTimeout(() => {
        buscarEmpresasOptions(input)
          .then(resolve)
          .catch(() => resolve([]));
      }, 300);
    });
}

export type EmpresaBuscaModo = 'razao' | 'cnpj' | 'id';

// Loader com modo (razao/cnpj/id) — usa o endpoint dedicado e grupo-scoped
// /contacil/empresas/busca. Pro toggle do seletor de empresa no Conciliar.
const buscarEmpresasModo = async (
  input: string,
  modo: EmpresaBuscaModo,
): Promise<IEmpresaOption[]> => {
  const res = await api.get<IApiEnvelope<IEmpresaApiItem[]>>('/api/v1/contacil/empresas/busca', {
    params: { q: input.trim(), modo, size: 20 },
  });
  const items = res.data?.data ?? [];
  return items.map(e => ({ value: e.id, label: empresaLabel(e), external_id: e.external_id }));
};

export function criarBuscaEmpresasModo(modo: EmpresaBuscaModo) {
  let timer: ReturnType<typeof setTimeout>;
  return (input: string): Promise<IEmpresaOption[]> =>
    new Promise(resolve => {
      clearTimeout(timer);
      if (!input.trim()) {
        resolve([]);
        return;
      }
      timer = setTimeout(() => {
        buscarEmpresasModo(input, modo)
          .then(resolve)
          .catch(() => resolve([]));
      }, 300);
    });
}

export const useContaCilImportar = () => {
  return useMutation({
    mutationFn: importarArquivo,
  });
};

export interface IContaCilImportStatus {
  import_id: number;
  status: IContaCilStatus;
  percent: number;
  fase_label?: string | null;
  detalhe?: string | null;
  erro?: string | null;
  qtd_transacoes?: number | null;
  qtd_novas?: number | null;
  qtd_duplicadas?: number | null;
  banco?: string | null;
  cnpj_detectado?: string | null;
  titular_detectado?: string | null;
}

const STATUS_FINAIS: IContaCilStatus[] = [
  'concluido',
  'erro',
  'a_identificar',
  'aguardando_titular',
  'aguardando_validacao',
  'quarentena',
];

// Apos este tempo em 'processando' o import e considerado "demorando". O
// reaper do backend vira pra 'erro' em ~20min; no front so desaceleramos o
// polling e mostramos um aviso — sem parar, pra captar a transicao do reaper.
export const IMPORT_WARN_MIN = 12;

// Minutos desde uma data ISO (Infinity se ausente/invalida -> trata como antigo).
export const minutosDesde = (iso?: string | null): number => {
  if (!iso) return Infinity;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return Infinity;
  return (Date.now() - t) / 60000;
};

/**
 * Polling do status de um import. Para de buscar ao chegar em estado final
 * (concluido/erro/a_identificar). Intervalo 2s enquanto processando.
 */
export const useContaCilImportStatus = (importId: number, enabled: boolean) =>
  useQuery({
    queryKey: ['contacil', 'import-status', importId] as const,
    queryFn: async (): Promise<IContaCilImportStatus> => {
      const res = await api.get<IApiEnvelope<IContaCilImportStatus>>(
        `/api/v1/contacil/importar/${importId}/status`,
      );
      return res.data.data;
    },
    enabled,
    refetchInterval: query => {
      const s = query.state.data?.status;
      if (s && STATUS_FINAIS.includes(s)) return false;
      // Backoff: depois de ~12min (360 polls de 2s) sem sair de 'processando',
      // desacelera pra 30s — evita polling agressivo eterno se o backend
      // travar, mas segue pollando pra captar o reaper marcando 'erro'.
      return (query.state.dataUpdateCount ?? 0) > 360 ? 30000 : 2000;
    },
  });

export interface IContaCilImportItem {
  import_id: number;
  nome_arquivo: string;
  formato: string;
  status: IContaCilStatus;
  banco?: string | null;
  empresa_id?: number | null;
  empresa_nome?: string | null;
  qtd_novas?: number | null;
  qtd_duplicadas?: number | null;
  cnpj_detectado?: string | null;
  titular_detectado?: string | null;
  saldo_inicial?: number | null;
  saldo_final?: number | null;
  balanco_ok?: boolean | null;
  divergencia_balanco?: number | null;
  importado_por_email?: string | null;
  importado_por_origem?: string | null;
  dt_criacao?: string | null;
}

/**
 * Lista persistente de imports (carrega do banco — sobrevive a refresh).
 * Refaz o fetch a cada 2,5s enquanto houver algum import processando.
 * `status` filtra (ex: 'a_identificar' pro contador do dashboard); `limit`
 * amplia o teto (visao "todas").
 */
export const useContaCilImports = (status?: IContaCilStatus, limit?: number) =>
  useQuery({
    queryKey: ['contacil', 'imports', status ?? 'all', limit ?? 'def'] as const,
    queryFn: async (): Promise<IContaCilImportItem[]> => {
      const params: Record<string, string | number> = {};
      if (status) params.status = status;
      if (limit) params.limit = limit;
      const res = await api.get<IApiEnvelope<IContaCilImportItem[]>>('/api/v1/contacil/importar/', {
        params: Object.keys(params).length ? params : undefined,
      });
      return res.data.data;
    },
    refetchInterval: query => {
      const arr = query.state.data;
      if (!Array.isArray(arr)) return false;
      const proc = arr.filter(i => i.status === 'processando');
      if (proc.length === 0) return false;
      // Se todos os 'processando' ja passaram do limite (travados), desacelera
      // pra 30s — segue pollando pra captar o reaper virar pra 'erro', sem
      // martelar a API a cada 2,5s indefinidamente.
      const algumRecente = proc.some(i => minutosDesde(i.dt_criacao) < IMPORT_WARN_MIN);
      return algumRecente ? 2500 : 30000;
    },
  });

/**
 * Invalida caches que dependem do estado de import: a lista de imports, a
 * lista/detalhe de extratos (atribuir muda de a_identificar -> concluido) e
 * a saude da empresa (extrato passa a contar).
 */
const useInvalidarImports = () => {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['contacil', 'imports'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'import-status'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'extratos'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'extrato-detalhe'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'empresas-saude'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos'] });
    qc.invalidateQueries({ queryKey: ['contacil', 'lancamentos-resumo'] });
  };
};

/**
 * Atribui manualmente uma empresa a um import 'a_identificar'. O backend
 * processa o parse guardado (categoriza + grava) e marca como concluido.
 */
export const useContaCilAtribuirEmpresa = () => {
  const invalidar = useInvalidarImports();
  return useMutation({
    mutationFn: async (params: {
      importId: number;
      empresaId: number;
    }): Promise<IContaCilImportStatus> => {
      const res = await api.patch<IApiEnvelope<IContaCilImportStatus>>(
        `/api/v1/contacil/importar/${params.importId}/empresa`,
        { empresa_id: params.empresaId },
      );
      return res.data.data;
    },
    onSuccess: invalidar,
  });
};
